import { post } from '../utilities';
import { API_URL_PREFIX, DEFAULT_TOKEN } from '../utilities/constants';

export interface PromptVersionResponse {
    apiStatus: string;
    apiMessage: string;
    output: {
        jobId: string;
        template: {
            templateId: string;
            name: string;
            latestVersion: number;
            activeVersion: number;
        };
        versions: Array<{
            versionId: string;
            version: number;
            prompt: {
                system: string;
                userTemplate: string;
            };
            status: string;
            isActive: boolean;
            createdAt: number;
            changelog: string;
            experimentName?: string;
        }>;
    };
}

export interface CreatePromptVersionResponse {
    apiStatus: string;
    apiMessage: string;
    output: {
        versionId: string | null;
        templateId: string | null;
        jobId: string | null;
        version: number | null;
        prompt: {
            system: string;
            userTemplate: string;
        } | null;
        changelog: string | null;
        status: string;
        experimentName: string | null;
        parentActiveVersionId: string | null;
        createdAt: number | null;
        poolingId?: string;
        message?: string;
    };
}

export interface ApplyPromptToAllResponse {
    apiStatus: string;
    apiMessage: string;
    output: {
        bulkJobId: string;
        jobId: string;
        promptVersionId: string;
        status: 'QUEUED' | 'PROCESSING';
        totalCandidates: number;
        message: string;
    };
}

export interface BulkEvaluationStatusResponse {
    apiStatus: string;
    apiMessage: string;
    output: {
        bulkJobId: string;
        jobId: string;
        promptVersionId: string;
        status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
        progress: {
            total: number;
            processed: number;
            successful: number;
            failed: number;
            pending: number;
            processing: number;
            percentComplete: number;
        };
        createdAt: number;
        startedAt: number;
        completedAt: number | null;
        lastUpdatedAt: number;
    };
}

export const getPromptVersions = (payload: {
    expertId: string;
    jobId: string;
    versionId?: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/getPromptVersions`, { ...payload, token: DEFAULT_TOKEN });
};

export const updateActivePromptVersion = (payload: {
    expertId: string;
    jobId: string;
    versionId: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/updateActivePromptVersion`, { ...payload, token: DEFAULT_TOKEN });
};

export const createPromptVersion = (payload: {
    expertId: string;
    jobId: string;
    prompt: {
        system: string;
        userTemplate: string;
    };
    changelog: string;
    status: 'DRAFT' | 'PUBLISHED';
    experimentName?: string;
    promptVersionId?: string;
    parentActiveVersionId?: string;
    async?: boolean;
    poolingId?: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/createPromptVersion`, { ...payload, token: DEFAULT_TOKEN });
};

export const deleteDraftPromptVersion = (payload: {
    expertId: string;
    jobId: string;
    versionId: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/deleteDraftPromptVersion`, { ...payload, token: DEFAULT_TOKEN });
};

export const applyPromptVersionToAll = (payload: {
    expertId: string;
    jobId: string;
    promptVersionId: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/applyPromptVersionToAll`, { ...payload, token: DEFAULT_TOKEN });
};

export const evaluateCandidatesData = (payload: {
    expertId: string;
    jobId: string;
    promptVersionId: string;
    parentActiveVersionId?: string;
    candidates: string[];
}) => {
    return post(`${API_URL_PREFIX}/V1/evaluateCandidatesData`, { ...payload, token: DEFAULT_TOKEN });
};

export const getCandidateEvaluationsByVersion = (payload: {
    expertId: string;
    jobId: string;
    versionId: string;
    candidates: string[];
}) => {
    return post(`${API_URL_PREFIX}/V1/getCandidateEvaluationsByVersion`, { ...payload, token: DEFAULT_TOKEN });
};

export const getBulkEvaluationStatus = (payload: {
    activePromptVersionId: string;
    expertId: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/getBulkEvaluationStatus`, { ...payload, token: DEFAULT_TOKEN });
};

export const cancelBulkEvaluation = (payload: {
    activePromptVersionId: string;
    expertId: string;
    bulkJobId: string;
}) => {
    return post(`${API_URL_PREFIX}/V1/cancelBulkEvaluation`, { ...payload, token: DEFAULT_TOKEN });
};

export const aiMatchingService = {
    getPromptVersions,
    updateActivePromptVersion,
    createPromptVersion,
    deleteDraftPromptVersion,
    applyPromptVersionToAll,
    evaluateCandidatesData,
    getCandidateEvaluationsByVersion,
    getBulkEvaluationStatus,
    cancelBulkEvaluation,
};
